import type { Tour, TourDetails } from '#domain/Tour.ts';
import { apiV2Root } from '#store/apiV2Root.ts';

import { mapTour, mapTourDetails, mapTours } from './adapters/tourAdapter.ts';
import { type ArchiveDownloadResult, downloadArchiveFromUrl } from './downloadArchive.ts';
import { type ApiWriteTour, type ApiWriteTourDuplicate, apiSchema } from './model/types.ts';
import { validateArrayItems, validateWarn } from './validateZodHelper.ts';

const TAG = 'TOURS';
const url = '/tours';

export type GetToursParams = {
    groupIds?: string[];
};

export type TourDocumentArchiveJobStatus = {
    tourId: string;
    status: 'IDLE' | 'RUNNING' | 'COMPLETED' | 'COMPLETED_PARTIAL' | 'FAILED';
    documentHash?: string;
    fileName?: string;
    partial: boolean;
    createdAt?: string;
    startedAt?: string;
    finishedAt?: string;
    expiresAt?: string;
    errorMessage?: string;
};

const buildGetToursQuery = ({ groupIds = [] }: GetToursParams = {}) => {
    if (groupIds.length === 0) {
        return url;
    }

    const params = new URLSearchParams();
    groupIds.forEach(groupId => {
        params.append('groupIds', groupId);
    });

    return `${url}?${params.toString()}`;
};

export const tourApiSlice = apiV2Root.injectEndpoints({
    endpoints: build => ({
        getTours: build.query<Tour[], GetToursParams | undefined>({
            query: params => buildGetToursQuery(params ?? undefined),
            providesTags: [TAG],
            transformResponse: (response, meta, arg) =>
                mapTours(validateArrayItems(apiSchema.TourListEntry)(response, meta, arg)),
        }),
        getTour: build.query<TourDetails, string>({
            query: tourId => `${url}/${tourId}`,
            providesTags: [TAG],
            transformResponse: (response, meta, arg) =>
                mapTourDetails(validateWarn(apiSchema.Tour)(response, meta, arg)),
        }),
        createTour: build.mutation<Tour, ApiWriteTour>({
            extraOptions: { maxRetries: 0 },
            query: tour => ({
                url: url,
                method: 'POST',
                body: JSON.stringify(tour),
            }),
            invalidatesTags: [TAG, 'ORDERS'],
            transformResponse: (response, meta, arg) => mapTour(validateWarn(apiSchema.Tour)(response, meta, arg)),
        }),
        duplicateTour: build.mutation<Tour, ApiWriteTourDuplicate>({
            query: duplicate => ({
                url: `${url}/duplicate`,
                method: 'POST',
                body: JSON.stringify(duplicate),
            }),
            invalidatesTags: [TAG, 'ORDERS'],
            transformResponse: (response, meta, arg) => mapTour(validateWarn(apiSchema.Tour)(response, meta, arg)),
        }),
        updateTour: build.mutation<Tour, { id: string; tour: ApiWriteTour }>({
            extraOptions: { maxRetries: 0 },
            query: ({ id, tour }) => ({
                url: `${url}/${id}`,
                method: 'PUT',
                body: JSON.stringify(tour),
            }),
            invalidatesTags: [TAG, 'ORDERS'],
            transformResponse: (response, meta, arg) => mapTour(validateWarn(apiSchema.Tour)(response, meta, arg)),
        }),
        deleteTour: build.mutation<void, string>({
            query: id => ({
                url: `${url}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [TAG],
        }),
        startTourDocumentArchiveJob: build.mutation<TourDocumentArchiveJobStatus, string>({
            query: tourId => ({
                url: `${url}/${tourId}/document-archive-job`,
                method: 'POST',
            }),
        }),
        getTourDocumentArchiveJob: build.query<TourDocumentArchiveJobStatus, string>({
            query: tourId => `${url}/${tourId}/document-archive-job`,
        }),
        downloadTourDocumentArchive: build.mutation<ArchiveDownloadResult, string>({
            query: tourId => ({
                url: `${url}/${tourId}/document-archive-job/download`,
                method: 'GET',
            }),
            transformResponse: (response: {
                fileName?: string;
                partial?: boolean;
                downloadUrl: string;
                expiresAt?: string;
            }) => {
                downloadArchiveFromUrl(response.downloadUrl, response.fileName);

                return {
                    fileName: response.fileName,
                    isPartial: response.partial === true,
                    downloadUrl: response.downloadUrl,
                    expiresAt: response.expiresAt,
                };
            },
        }),
    }),
});

export const {
    useGetToursQuery,
    useCreateTourMutation,
    useDuplicateTourMutation,
    useUpdateTourMutation,
    useGetTourQuery,
    useGetTourDocumentArchiveJobQuery,
    useDeleteTourMutation,
    useStartTourDocumentArchiveJobMutation,
    useLazyGetTourDocumentArchiveJobQuery,
    useDownloadTourDocumentArchiveMutation,
} = tourApiSlice;
